import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectSpecialistRoleToWork } from "../../app/actions/userRoleActions";
import { SPECIALIST_ROLE_CARD_CONFIG } from "../../app/helpers/activeSpecialistRoleHelper";
import { getRoleDisplayLabel } from "../../app/helpers/specialistRoleModuleHelper";
import useActiveSpecialistRole, { getActiveSpecialistRoleLabel } from "../../app/hooks/useActiveSpecialistRole";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import NavbarIconsGenericComponent from "../generic-nav-bar/NavbarIconsGenericComponent";

const SpecialistRoleSelectionScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { roleLogin } = useSelector((state) => state.userRole);
  const {
    availableRoles,
    selectedRole,
    roleKey: selectedRoleKey,
    flowEngineEnabled
  } = useActiveSpecialistRole();

  useEffect(() => {
    if (!flowEngineEnabled) {
      return;
    }

    if (availableRoles.length === 1 && !roleLogin?.isRoleSpecialistSelected) {
      dispatch(selectSpecialistRoleToWork(availableRoles[0]));
    }
  }, [availableRoles, dispatch, flowEngineEnabled, roleLogin?.isRoleSpecialistSelected]);

  const operatorRoles = useMemo(
    () => availableRoles.filter((role) => role.card?.isOperator),
    [availableRoles]
  );

  const viewerRoles = useMemo(
    () => availableRoles.filter((role) => !role.card?.isOperator),
    [availableRoles]
  );

  const handleSelectRole = (role) => {
    if (!role) {
      return;
    }

    dispatch(selectSpecialistRoleToWork(role));
    Swal.fire(
      t("titles.success"),
      t(
        "specialist.selection.roleSelected",
        "Rol seleccionado. Ahora puedes operar con las pantallas correspondientes."
      ),
      "success"
    );
  };

  const handleGoToDestination = (role) => {
    if (!role?.card?.route) {
      return;
    }

    if (selectedRoleKey !== role.roleKey) {
      dispatch(selectSpecialistRoleToWork(role));
    }

    navigate(role.card.route);
  };

  const renderRoleCard = (role) => {
    const card = role.card || SPECIALIST_ROLE_CARD_CONFIG[role.roleKey];
    const isActive = selectedRoleKey === role.roleKey;

    return (
      <div className="col-12 col-md-6 col-xl-3" key={role.id}>
        <div className={`card h-100 shadow-sm ${isActive ? "border-primary" : ""}`}>
          <div className="card-body d-flex flex-column">
            <span className={`badge mb-2 ${card.isOperator ? "bg-primary" : "bg-secondary"}`}>
              {t(card.badgeKey, card.badgeFallback)}
            </span>
            <h5 className="card-title">{getRoleDisplayLabel(role.name, t)}</h5>
            <p className="card-text flex-grow-1">
              {t(card.descriptionKey, card.descriptionFallback)}
            </p>
            <button
              type="button"
              className={`btn ${isActive ? "btn-success" : "btn-outline-primary"}`}
              onClick={() => handleSelectRole(role)}
              disabled={isActive}
            >
              {isActive
                ? t("specialist.selection.role.current", "Rol activo")
                : t("specialist.selection.role.select", "Trabajar con este rol")}
            </button>
            {card.route && (
              <button
                type="button"
                className="btn btn-link p-0 mt-2 align-self-start"
                onClick={() => handleGoToDestination(role)}
              >
                {card.isOperator
                  ? t("specialist.selection.role.goToInbox", "Ir a la bandeja")
                  : t("specialist.selection.role.goToModules", "Ir a módulos")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <FlowEngineGlobalGate>
      <NavbarIconsGenericComponent componentName="SpecialistRoleSelection" />
      <div className="container py-4">
        <div className="mb-4">
          <h1 className="mb-1">{t("specialist.selection.role.title", "Selecciona el rol especialista")}</h1>
          <p className="text-muted mb-0">
            {t(
              "specialist.selection.role.subtitle",
              "Elige con qué rol deseas trabajar. El rol activo se mantiene en sesión y puedes cambiarlo desde la barra superior."
            )}
          </p>
        </div>

        <div className="mb-4">
          <small className="text-muted">
            {t("specialist.selection.role.activeLabel", "Rol activo:")}{" "}
            {getActiveSpecialistRoleLabel(selectedRole, t)}
          </small>
        </div>

        <div className="alert alert-info mb-4">
          <strong>{t("specialist.selection.role.noticeTitle", "Importante")}</strong>
          <p className="mb-0">
            {t(
              "specialist.selection.role.noticeBody",
              "Especialista actúa como visualizador. Gestor, Validador y Editor son operadores del flujo de aprobaciones."
            )}
          </p>
        </div>

        {availableRoles.length === 0 ? (
          <div className="alert alert-secondary">
            {t(
              "specialist.selection.role.empty",
              "No se encontraron roles especialistas disponibles para tu usuario."
            )}
          </div>
        ) : (
          <>
            {viewerRoles.length > 0 && (
              <section className="mb-4">
                <h2 className="h5 mb-3">
                  {t("specialist.selection.role.viewerSection", "Rol visualizador")}
                </h2>
                <div className="row g-4">{viewerRoles.map(renderRoleCard)}</div>
              </section>
            )}

            {operatorRoles.length > 0 && (
              <section className="mb-4">
                <h2 className="h5 mb-3">
                  {t("specialist.selection.role.operatorSection", "Roles operadores del flujo")}
                </h2>
                <div className="row g-4">{operatorRoles.map(renderRoleCard)}</div>
              </section>
            )}
          </>
        )}
      </div>
    </FlowEngineGlobalGate>
  );
};

export default SpecialistRoleSelectionScreen;
